/* @jsx h */
import {
  defineComponent,
  h,
  PropType,
  ref,
  computed,
  VNode,
  Fragment,
} from "vue";
import { RightSheet } from "../RightSheet";

export type ResourceTone =
  | "neutral"
  | "primary"
  | "secondary"
  | "accent"
  | "info"
  | "success"
  | "warning"
  | "error";

type ResourceSheetMode = "create" | "edit";

export type ResourceMetaDescriptor = {
  icon?: string;
  label: string;
  helper?: string;
  tone?: ResourceTone;
};

export type ResourceTagDescriptor = {
  label: string;
  tone?: ResourceTone;
  variant?: "solid" | "outline" | "soft";
};

export type ResourceStatusDescriptor = {
  label: string;
  tone?: ResourceTone;
  variant?: "solid" | "outline" | "soft";
};

export type ResourceActionDescriptor = {
  label: string;
  icon?: string;
  tone?: ResourceTone;
  onClick?: (event: MouseEvent) => void;
};

export type ResourceListItemDescriptor = {
  id?: string | number;
  title: string;
  subtitle?: string;
  description?: string;
  leading?: () => VNode;
  trailing?: () => VNode;
  status?: ResourceStatusDescriptor;
  tags?: ResourceTagDescriptor[];
  meta?: ResourceMetaDescriptor[];
  footer?: () => VNode;
  actions?: ResourceActionDescriptor[];
};

export type ResourceDetailContext<T> = {
  mode: ResourceSheetMode;
  item: T | null;
  close: () => void;
};

export type ResourceSheetTitleContext<T> = {
  mode: ResourceSheetMode;
  item: T | null;
};

export type ResourceListItemContext<T> = {
  index: number;
  openDetail: () => void;
  item: T;
};

export type ResourceMasterDetailProps<T> = {
  title: string;
  description?: string | VNode;
  resourceName?: string;
  items: T[];
  createLabel?: string;
  disableCreate?: boolean;
  sheetWidth?: string;
  renderListItem: (
    item: T,
    context: ResourceListItemContext<T>,
  ) => ResourceListItemDescriptor;
  renderDetail: (context: ResourceDetailContext<T>) => VNode;
  renderEmpty?: () => VNode;
  getKey?: (item: T, index: number) => string | number;
  getSheetTitle?: (context: ResourceSheetTitleContext<T>) => string | VNode;
  disableBottomSheet?: boolean;
};

const badgeClassMap: Record<
  ResourceTone,
  { solid: string; outline: string; soft: string }
> = {
  neutral: {
    solid: "badge",
    outline: "badge badge-outline",
    soft: "badge badge-outline",
  },
  primary: {
    solid: "badge badge-primary",
    outline: "badge badge-outline text-primary",
    soft: "badge badge-outline text-primary",
  },
  secondary: {
    solid: "badge badge-secondary",
    outline: "badge badge-outline text-secondary",
    soft: "badge badge-outline text-secondary",
  },
  accent: {
    solid: "badge badge-accent",
    outline: "badge badge-outline text-accent",
    soft: "badge badge-outline text-accent",
  },
  info: {
    solid: "badge badge-info",
    outline: "badge badge-outline text-info",
    soft: "badge badge-outline text-info",
  },
  success: {
    solid: "badge badge-success",
    outline: "badge badge-outline text-success",
    soft: "badge badge-outline text-success",
  },
  warning: {
    solid: "badge badge-warning",
    outline: "badge badge-outline text-warning",
    soft: "badge badge-outline text-warning",
  },
  error: {
    solid: "badge badge-error",
    outline: "badge badge-outline text-error",
    soft: "badge badge-outline text-error",
  },
};

const actionClassMap: Record<ResourceTone, string> = {
  neutral: "btn btn-ghost btn-sm",
  primary: "btn btn-ghost btn-sm ",
  secondary: "btn btn-ghost btn-sm text-secondary",
  accent: "btn btn-ghost btn-sm text-accent",
  info: "btn btn-ghost btn-sm text-info",
  success: "btn btn-ghost btn-sm text-success",
  warning: "btn btn-ghost btn-sm text-warning",
  error: "btn btn-ghost btn-sm text-error",
};

const toneToBadgeClass = (
  tone: ResourceTone | undefined,
  variant: "solid" | "outline" | "soft" = "soft",
) => {
  const key = tone ?? "neutral";
  return badgeClassMap[key][variant];
};

const toneToActionClass = (tone: ResourceTone | undefined) => {
  const key = tone ?? "neutral";
  return actionClassMap[key];
};

const renderDescriptorContent = (descriptor: ResourceListItemDescriptor) => {
  const {
    leading,
    title,
    subtitle,
    description,
    status,
    tags,
    meta,
    trailing,
    actions,
  } = descriptor;

  return (
    <Fragment>
      <div class="flex flex-col gap-4 p-5">
        <div class="flex items-start gap-4">
          {leading ? (
            <div class="shrink-0 rounded-2xl bg-base-200/80 border border-base-300/70 p-4 flex items-center justify-center">
              {leading()}
            </div>
          ) : null}

          <div class="flex-1 space-y-3">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="space-y-1">
                <h3 class="md:text-lg font-semibold text-accent">{title}</h3>
                {subtitle ? (
                  <p class="text-sm text-base-content/70">{subtitle}</p>
                ) : null}
              </div>
              {status ? (
                <span class={toneToBadgeClass(status.tone, status.variant)}>
                  {status.label}
                </span>
              ) : trailing ? (
                <div class="shrink-0">{trailing()}</div>
              ) : null}
            </div>

            {tags && tags.length ? (
              <div class="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag.label}
                    class={toneToBadgeClass(tag.tone, tag.variant)}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            ) : null}

            {description ? (
              <p class="text-sm leading-relaxed text-base-content/80">
                {description}
              </p>
            ) : null}
          </div>
        </div>

        {meta && meta.length ? (
          <div class="flex flex-wrap gap-3 text-sm text-base-content/70">
            {meta.map((item) => (
              <div
                key={item.label}
                class="inline-flex items-center gap-2 rounded-full bg-base-200/60 px-3 py-1.5"
              >
                {item.icon ? (
                  <span class="material-symbols-outlined text-base">
                    {item.icon}
                  </span>
                ) : null}
                <span class="font-medium text-base-content">{item.label}</span>
                {item.helper ? (
                  <span class="text-xs text-base-content/60">
                    {item.helper}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {actions && actions.length ? (
          <div class="flex flex-wrap gap-2">
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                class={toneToActionClass(action.tone)}
                onClick={(event: MouseEvent) => {
                  event.stopPropagation();
                  action.onClick?.(event);
                }}
              >
                {action.icon ? (
                  <span class="material-symbols-outlined text-base">
                    {action.icon}
                  </span>
                ) : null}
                <span>{action.label}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {descriptor.footer ? (
        <div class="border-t border-base-200 bg-base-200/40 px-5 py-4">
          {descriptor.footer()}
        </div>
      ) : null}
    </Fragment>
  );
};

const DefaultEmptyState = (copy: {
  title: string;
  actionLabel: string;
  onClick: () => void;
  disableAction: boolean;
}) => (
  <div class="flex flex-1 items-center justify-center py-20">
    <div class="max-w-md space-y-4 text-center">
      <h3 class="text-xl font-semibold text-accent">{copy.title}</h3>
      <p class="text-base-content/70">
        Get started by creating your first entry. You can always update or
        remove it later.
      </p>
      {copy.disableAction ? null : (
        <button
          type="button"
          class="btn btn-primary mt-2"
          onClick={copy.onClick}
        >
          <span class="material-symbols-outlined text-base">add</span>
          <span>{copy.actionLabel}</span>
        </button>
      )}
    </div>
  </div>
);

export const ResourceMasterDetail = defineComponent<
  ResourceMasterDetailProps<unknown>
>({
  name: "ResourceMasterDetail",
  props: {
    title: { type: String, required: true },
    description: { type: [String, Object] as PropType<string | VNode> },
    resourceName: { type: String, default: "Item" },
    items: {
      type: Array as PropType<unknown[]>,
      default: () => [],
    },
    createLabel: { type: String, default: "" },
    disableCreate: { type: Boolean, default: false },
    sheetWidth: { type: String, default: "480px" },
    renderListItem: {
      type: Function as PropType<
        (
          item: unknown,
          context: ResourceListItemContext<unknown>,
        ) => ResourceListItemDescriptor
      >,
      required: true,
    },
    renderDetail: {
      type: Function as PropType<
        (context: ResourceDetailContext<unknown>) => VNode
      >,
      required: true,
    },
    renderEmpty: {
      type: Function as PropType<() => VNode>,
    },
    getKey: {
      type: Function as PropType<
        (item: unknown, index: number) => string | number
      >,
    },
    getSheetTitle: {
      type: Function as PropType<
        (context: ResourceSheetTitleContext<unknown>) => string | VNode
      >,
    },
    disableBottomSheet: { type: Boolean, default: false },
  },
  setup(props) {
    const sheetOpen = ref(false);
    const sheetMode = ref<ResourceSheetMode>("create");
    const activeItem = ref<unknown | null>(null);

    const defaultCreateLabel = computed(
      () => props.createLabel || `Add ${props.resourceName}`,
    );

    const disableBottomSheet = computed(
      () => props.disableBottomSheet === true,
    );

    const sheetTitle = computed(() => {
      const context: ResourceSheetTitleContext<unknown> = {
        mode: sheetMode.value,
        item: sheetMode.value === "edit" ? activeItem.value : null,
      };

      if (props.getSheetTitle) {
        return props.getSheetTitle(context);
      }

      const baseName = props.resourceName || "Item";
      return sheetMode.value === "create"
        ? `New ${baseName}`
        : `Edit ${baseName}`;
    });

    const openCreate = () => {
      sheetMode.value = "create";
      activeItem.value = null;
      sheetOpen.value = true;
    };

    const openEdit = (item: unknown) => {
      sheetMode.value = "edit";
      activeItem.value = item;
      sheetOpen.value = true;
    };

    const closeSheet = () => {
      sheetOpen.value = false;
    };

    return () => {
      const list = props.items || [];
      const sheetContext: ResourceDetailContext<unknown> = {
        mode: sheetMode.value,
        item: sheetMode.value === "edit" ? (activeItem.value as unknown) : null,
        close: closeSheet,
      };

      return (
        <div class="flex h-full min-h-[500px] w-full flex-col overflow-hidden rounded-3xl border border-base-300/70 bg-base-100/80 shadow-lg backdrop-blur">
          <div class="flex flex-col gap-4 border-b border-base-300/80 bg-base-200/50 px-6 py-5 md:flex-row md:items-center md:justify-between">
            <div class="space-y-2">
              <h2 class="text-2xl font-semibold text-accent">{props.title}</h2>
              {props.description ? (
                typeof props.description === "string" ? (
                  <p class="text-sm text-base-content/70">
                    {props.description}
                  </p>
                ) : (
                  props.description
                )
              ) : null}
            </div>
            {props.disableCreate ? null : (
              <button
                type="button"
                class="btn btn-primary btn-sm md:btn-md"
                onClick={openCreate}
              >
                <span class="material-symbols-outlined text-base">add</span>
                <span>{defaultCreateLabel.value}</span>
              </button>
            )}
          </div>

          <div class="flex-1 overflow-y-auto px-6 py-6">
            {list.length === 0 ? (
              props.renderEmpty ? (
                props.renderEmpty()
              ) : (
                <DefaultEmptyState
                  title={`You don't have any ${props.title.toLowerCase()} yet.`}
                  actionLabel={defaultCreateLabel.value}
                  onClick={openCreate}
                  disableAction={props.disableCreate === true}
                />
              )
            ) : (
              <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {list.map((item, index) => {
                  const descriptor = props.renderListItem(item, {
                    index,
                    openDetail: () => openEdit(item),
                    item,
                  });
                  const key =
                    props.getKey?.(item, index) ??
                    descriptor.id ??
                    index.toString();

                  return (
                    <div
                      key={key}
                      class="group h-full cursor-pointer overflow-hidden rounded-2xl border border-base-300/70 bg-base-100/90 shadow-sm transition hover:-translate-y-1 hover:border-primary/60 hover:shadow-xl"
                      onClick={() => openEdit(item)}
                    >
                      {renderDescriptorContent(descriptor)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <RightSheet
            isOpen={sheetOpen.value}
            title={sheetTitle.value}
            onClose={closeSheet}
            width={props.sheetWidth}
            disableBottomSheet={disableBottomSheet.value}
            class="max-h-screen"
          >
            <div class="flex h-full flex-col overflow-hidden">
              {props.renderDetail(sheetContext)}
            </div>
          </RightSheet>
        </div>
      );
    };
  },
});
